// opsTables.tsx
import { useEffect, useMemo, useState } from 'react';
import { useApiData } from '../../contexts/apiService';
import './opsTables.css';
import Dropdown from '../../components/common/dropdown/dropdown';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import LoaderWheel from '../../components/common/LoaderWheel';
import SQLTable from '../../components/sql_table/tableMain';
import { getUniqueDropdownOptions } from '../../helper_functions';
import TableInput from '../../components/common/table_input/tableInput';
import { ArrowRight, ArrowLeft, BarChart3, Table } from 'lucide-react';
import DailySummary from '../DailySummary/dailySummary';
import {
    LeaseCoordinateItem,
    StationCoordinateItem,
    PumperContactItem,
    CarrierItem,
    NameCorrectionsItem,
    TruckingRateItem,
    ApprovedMileageItem,
    CustomerReportsItem,
    MobileReqMailingListItem,
    NominationsItem,
    MarketPriceItem,
    CarrierDefinitionItem,
    CalculatedRoutesItem,
    ProducerDefinitionItem,
    EntityDefinitionItem,
} from '../../contexts/tableDefinitions';
import { useDataManager } from '../../contexts/dataManager';
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';
import {
    getColConfigs,
    getInputConfigs,
    MONTH_OPTIONS,
    type InputConfig
} from './tableConfig';

import { getNameCorrectionsConfig } from './ops_tables_config/name_corrections';


// Define the ApiData interface
interface ApiData {
    lease_coordinates: LeaseCoordinateItem[];
    market_coordinates: StationCoordinateItem[];
    pumper_contact: PumperContactItem[];
    name_corrections: NameCorrectionsItem[];
    trucking_rates: TruckingRateItem[];
    approved_mileage: ApprovedMileageItem[];
    main_table: CarrierItem[];
    customer_reports: CustomerReportsItem[];
    mobile_req_mailing_list: MobileReqMailingListItem[];
    nominations: NominationsItem[];
    market_discounts: MarketPriceItem[];
    carrier_definition: CarrierDefinitionItem[];
    calculated_routes: CalculatedRoutesItem[];
    producer_definition: ProducerDefinitionItem[];
    entity_definition: EntityDefinitionItem[];

    carrier_cptransport: CarrierItem[];
    carrier_diamondb: CarrierItem[];
    carrier_endeavor: CarrierItem[];
    carrier_guardian: CarrierItem[];
    carrier_heartriver: CarrierItem[];
    carrier_heartrivernd: CarrierItem[];
    carrier_penley: CarrierItem[];
    carrier_southhaven: CarrierItem[];
    carrier_texon: CarrierItem[];
    carrier_opa: CarrierItem[];
    carrier_emmaus: CarrierItem[];
    carrier_troy: CarrierItem[];
}


const OpsTables = () => {
    const { apiData, isLoading } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    const [activeTable, setActiveTable] = useState<string>('lease_coordinates');
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [hasAdminAccess, setHasAdminAccess] = useState<boolean>(false);
    const [nameCorrectionsValues, setNameCorrectionsValues] = useState<Record<string, any>>({});
    const [nominationsFilter, setNominationsFilter] = useState<string>('all'); // 'all' or 'Month Year' format
    const [showBarChart, setShowBarChart] = useState<boolean>(false);
    const { userRole = 'viewer' } = useUserAccess();

    useEffect(() => {
        // Request data for tables
        if (activeTable === 'lease_coordinates') {
            if (!hasTableData('producer_definition')) {
                requestTableData('producer_definition');
            }
            if (!hasTableData('crude_tanks_definition')) {
                requestTableData('crude_tanks_definition');
            }
            if (!hasTableData('entity_definition')) {
                requestTableData('entity_definition');
            }
        }
        if (activeTable === 'name_corrections') {
            if (!hasTableData('market_coordinates')) {
                requestTableData('market_coordinates');
            }
            if (!hasTableData('producer_definition')) {
                requestTableData('producer_definition');
            }
            if (!hasTableData('lease_coordinates')) {
                requestTableData('lease_coordinates');
            }
        }
        if (activeTable === 'entity_definition') {
            if (!hasTableData('entity_producer_history')) {
                requestTableData('entity_producer_history');
            }
        }
    }, [activeTable]);

    // Get unique dropdown options for producer_id
    const producerOptions = getUniqueDropdownOptions({
        tableData: apiData?.producer_definition,
        valueColumn: 'id',
        labelColumn: 'producer_name',
    });
    
    // Get destination options for input nominations
    const destinationOptions = getUniqueDropdownOptions({
        tableData: apiData?.market_coordinates,
        valueColumn: 'id',
        labelColumn: 'station'
    });

    // Get station owner options for input nominations
    const stationOwnerOptions = getUniqueDropdownOptions({
        tableData: apiData?.market_discounts,
        valueColumn: 'station_owner',
        labelColumn: 'station_owner'
    });

    // Get all lease options with producer information
    const leaseOptions = getUniqueDropdownOptions({
        tableData: apiData?.lease_coordinates,
        valueColumn: 'id',
        labelColumn: 'lease',
        extraFields: ['producer_id', 'producer_name']
    });

    const entityOptions = getUniqueDropdownOptions({
        tableData: apiData?.entity_definition,
        valueColumn: 'id',
        labelColumn: 'entity_name',
        extraFields: ['current_producer_id']
    });

    // Build a lookup map from lease to producer to avoid O(n^2) .find calls
    const leaseProducerByLease: Record<string, string> = {};
    (apiData?.lease_coordinates ?? []).forEach(item => {
        if (item.lease && leaseProducerByLease[item.lease] === undefined) {
            leaseProducerByLease[item.lease] = item.producer || '';
        }
    });

    // Generate nominations filter options (All Nominations, or specific Month Year)
    const nominationsFilterOptions = useMemo(() => {
        const options = [{ value: 'all', label: 'All Nominations' }];

        if (apiData?.nominations && apiData.nominations.length > 0) {
            // Get unique month-year combinations
            const uniqueMonthYears = new Set<string>();

            apiData.nominations.forEach(item => {
                if (item.month && item.year) {
                    uniqueMonthYears.add(`${item.month} ${item.year}`);
                }
            });

            // Convert to array and sort by year (descending) then month (descending)
            const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

            const sortedMonthYears = Array.from(uniqueMonthYears).sort((a, b) => {
                const [monthA, yearA] = a.split(' ');
                const [monthB, yearB] = b.split(' ');

                // First compare by year (descending - most recent first)
                if (yearA !== yearB) {
                    return parseInt(yearB) - parseInt(yearA);
                }
                // Then compare by month (descending - most recent month first within same year)
                return monthOrder.indexOf(monthB) - monthOrder.indexOf(monthA);
            });

            sortedMonthYears.forEach(monthYear => {
                options.push({ value: monthYear, label: monthYear });
            });
        }

        return options;
    }, [apiData?.nominations]);

    // Filter nominations data based on selected filter
    const filteredNominationsData = useMemo(() => {
        if (!apiData?.nominations) return [];

        if (nominationsFilter === 'all') {
            return apiData.nominations;
        }

        // Filter by selected month and year
        const [selectedMonth, selectedYear] = nominationsFilter.split(' ');
        return apiData.nominations.filter(item =>
            item.month === selectedMonth && item.year === selectedYear
        );
    }, [apiData?.nominations, nominationsFilter]);

    // Helper function to get days in a month
    const getDaysInMonth = (month: string, year: string): number => {
        if (!month || !year) return 30;

        const monthIndex = {
            'January': 0, 'February': 1, 'March': 2, 'April': 3,
            'May': 4, 'June': 5, 'July': 6, 'August': 7,
            'September': 8, 'October': 9, 'November': 10, 'December': 11
        }[month];

        if (monthIndex === undefined) return 30;

        const yearNum = parseInt(year);
        return new Date(yearNum, monthIndex + 1, 0).getDate();
    };

    // Generate year options (current year - 1 to current year + 5)
    const generateYearOptions = () => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 7 }, (_, i) => {
            const year = (currentYear - 1 + i).toString();
            return { value: year, label: year };
        });
    };

    // Generate nominations config with cascading dropdowns
    const getNominationsConfig = (
        stationOwnerOptions: any[],
        apiData: any
    ): InputConfig => {
        return {
            title: 'Nominations',
            columns: [
                { colName: 'station_owner', placeholder: 'Station Owner', colType: 'text', required: true, inputType: 'dropdown', dependsOn:'station', dropdownOptions: stationOwnerOptions },
                {
                    colName: 'station', placeholder: 'Station', colType: 'text', required: true, inputType: 'dropdown',
                    dropdownOptions: [],
                    dependsOn: 'station_owner',
                    getFilteredOptions: (selectedStationOwner: string) => {
                        if (!apiData?.market_discounts || !selectedStationOwner) return [];

                        const filteredStations = apiData.market_discounts
                            .filter((item: any) => item.station_owner === selectedStationOwner)
                            .map((item: any) => item.station);

                        const uniqueStations = Array.from(new Set(filteredStations));

                        return uniqueStations.map(station => ({
                            value: String(station),
                            label: String(station)
                        })).sort((a: any, b: any) => a.label.localeCompare(b.label));
                    }
                },
                {
                    colName: 'market', placeholder: 'Market', colType: 'text', required: true, inputType: 'dropdown',
                    dropdownOptions: [],
                    dependsOn: 'station',
                    getFilteredOptions: (selectedStation: string) => {
                        if (!apiData?.market_discounts || !selectedStation) return [];

                        const filteredMarkets = apiData.market_discounts
                            .filter((item: any) => item.station === selectedStation)
                            .map((item: any) => item.market);

                        const uniqueMarkets = Array.from(new Set(filteredMarkets));

                        return uniqueMarkets.map(market => ({
                            value: String(market),
                            label: String(market)
                        })).sort((a: any, b: any) => a.label.localeCompare(b.label));
                    }
                },
                { colName: 'month', placeholder: 'Month', colType: 'text', required: true, inputType: 'dropdown', dropdownOptions: MONTH_OPTIONS },
                { colName: 'year', placeholder: 'Year', colType: 'text', required: true, inputType: 'dropdown', dropdownOptions: generateYearOptions() },
                {
                    colName: 'nomination_monthly', placeholder: 'Monthly Nomination', colType: 'number', required: true,
                    calculateValue: (values: Record<string, any>, modifiedField?: string, masterField?: string | null) => {
                        // If nomination_bpd is the master field (user modified it last), calculate from it
                        if (modifiedField === 'nomination_bpd' ||
                            (masterField === 'nomination_bpd' && (modifiedField === 'month' || modifiedField === 'year'))) {
                            const bpd = parseFloat(values.nomination_bpd);
                            const daysInMonth = getDaysInMonth(values.month, values.year);
                            if (!isNaN(bpd) && bpd > 0 && daysInMonth) {
                                return (bpd * daysInMonth).toFixed(2);
                            }
                        }
                        return values.nomination_monthly;
                    }
                },
                {
                    colName: 'nomination_bpd', placeholder: 'Nomination BPD', colType: 'number', required: true,
                    calculateValue: (values: Record<string, any>, modifiedField?: string, masterField?: string | null) => {
                        // If nomination_monthly is the master field (user modified it last), calculate from it
                        if (modifiedField === 'nomination_monthly' ||
                            (masterField === 'nomination_monthly' && (modifiedField === 'month' || modifiedField === 'year'))) {
                            const monthly = parseFloat(values.nomination_monthly);
                            const daysInMonth = getDaysInMonth(values.month, values.year);
                            if (!isNaN(monthly) && monthly > 0 && daysInMonth) {
                                return (monthly / daysInMonth).toFixed(2);
                            }
                        }
                        return values.nomination_bpd;
                    }
                },
            ]
        };
    };

    // RBAC: Grant access based on user role
    const showTable = Number(USER_ROLE_LEVEL[userRole]) >= 1;
    const SCHEDULER = USER_ROLE_LEVEL['scheduler'];
    const showInputs = Number(USER_ROLE_LEVEL[userRole]) == SCHEDULER || Number(USER_ROLE_LEVEL[userRole]) == 4 || Number(USER_ROLE_LEVEL[userRole]) == 6 || Number(USER_ROLE_LEVEL[userRole]) == 7 ;

    const ADMIN_ONLY_TABLES = [
        'mobile_req_mailing_list',
        'customer_reports',
    ];

    // state to track user status
    useEffect(() => {
        const auth = getAuth();
        const db = getFirestore();

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setHasAdminAccess(userData.role === 'admin' || userData.role === 'super_admin');

                    // If user loses admin access and is on an admin-only table, redirect to a non-admin table
                    if (!hasAdminAccess) {
                        if (ADMIN_ONLY_TABLES.includes(activeTable)) {
                            setActiveTable('lease_coordinates');
                        }
                    }
                }
            } else {
                setHasAdminAccess(false);
                // Reset to non-admin table if logged out
                if (ADMIN_ONLY_TABLES.includes(activeTable)) {
                    setActiveTable('lease_coordinates');
                }
            }
        });

        return () => unsubscribe();
    }, [activeTable]);

    if (isLoading) return (
        <LoaderWheel />
    );

    const SQL_TABLE_CONFIGS = getColConfigs(entityOptions, producerOptions);

    const tableOptions = Object.entries(SQL_TABLE_CONFIGS)
        .filter(([tableName, _]) => !ADMIN_ONLY_TABLES.includes(tableName) || hasAdminAccess)
        .map(([value, config]) => ({
            value,
            label: config.tableName
        }));

    return (
        <div className="opsTables-mainContainer">
            {!(activeTable === 'nominations' && showBarChart) && (
                <div className="opsTables-topContainer">
                    <div className="opsTables-dropdownContainer">
                        <Dropdown
                            options={tableOptions}
                            value={activeTable}
                            onChange={(value) => {
                                setActiveTable(value);
                                setSelectedRows(new Set());
                            }}
                            className="dropdown-inputMatch"
                        />
                    </div>
                    <div className="opsTables-toolbarContainer">
                        {showInputs && (activeTable === 'nominations') ? (
                            <TableInput
                                tableName={activeTable}
                                inputColumnConfigs={getNominationsConfig(stationOwnerOptions, apiData).columns}
                            />
                        ) : activeTable === 'name_corrections' ? (
                            <TableInput
                                tableName={activeTable}
                                inputColumnConfigs={getNameCorrectionsConfig(
                                    producerOptions,
                                    leaseOptions,
                                    destinationOptions,
                                    nameCorrectionsValues
                                ).columns}
                                onValuesChange={setNameCorrectionsValues}
                            />
                        ) : (
                            <TableInput
                                tableName={activeTable}
                                inputColumnConfigs={getInputConfigs(activeTable, producerOptions, entityOptions, leaseOptions)[activeTable].columns}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Filter navigation for nominations - below inputs, centered */}
            {activeTable === 'nominations' && !showBarChart && (
                <div className="opsTables-filterNavigation">
                    <button
                        onClick={() => {
                            const currentIndex = nominationsFilterOptions.findIndex(opt => opt.value === nominationsFilter);
                            if (currentIndex < nominationsFilterOptions.length - 1) {
                                setNominationsFilter(nominationsFilterOptions[currentIndex + 1].value);
                            }
                        }}
                        disabled={nominationsFilterOptions.findIndex(opt => opt.value === nominationsFilter) >= nominationsFilterOptions.length - 1}
                        title="Previous"
                    >
                        <ArrowLeft />
                    </button>
                    <Dropdown
                        options={nominationsFilterOptions}
                        value={nominationsFilter}
                        onChange={(value) => setNominationsFilter(value)}
                        className="dropdown-inputMatch"
                        width={150}
                        sortOrder="none"
                    />
                    <button
                        onClick={() => {
                            const currentIndex = nominationsFilterOptions.findIndex(opt => opt.value === nominationsFilter);
                            if (currentIndex > 0) {
                                setNominationsFilter(nominationsFilterOptions[currentIndex - 1].value);
                            }
                        }}
                        disabled={nominationsFilterOptions.findIndex(opt => opt.value === nominationsFilter) <= 0}
                        title="Next"
                    >
                        <ArrowRight />
                    </button>
                </div>
            )}

            {activeTable === 'nominations' && showBarChart ? (
                <div className="opsTables-barChartContainer">
                    <div className="opsTables-barChartToolbar">
                        <button
                            className="opsTables-viewToggleButton"
                            onClick={() => setShowBarChart(false)}
                        >
                            <Table size={16} />
                            View Table
                        </button>
                    </div>
                    <DailySummary />
                </div>
            ) : (
                <div className="opsTables-tableContainer">
                    {activeTable === 'nominations' && (
                        <button
                            className="opsTables-viewToggleButton opsTables-viewToggleButton--overlay"
                            onClick={() => setShowBarChart(true)}
                        >
                            <BarChart3 size={16} />
                            View Chart
                        </button>
                    )}
                    {SQL_TABLE_CONFIGS[activeTable] && (
                        <SQLTable
                            tableName={activeTable}
                            tableData={activeTable === 'nominations' ? filteredNominationsData :
                            (activeTable === 'lease_coordinates' || activeTable === 'market_coordinates')
                                ? (apiData?.[activeTable as keyof ApiData] || []).filter((row: any) => !row.created_in_error)
                                : (apiData?.[activeTable as keyof ApiData] || [])}
                            idColumn={SQL_TABLE_CONFIGS[activeTable].idColumn}
                            columnConfigs={SQL_TABLE_CONFIGS[activeTable].columnConfigs}
                            jsonColumnConfig={SQL_TABLE_CONFIGS[activeTable].jsonColumnConfig}
                            showHeader={true}
                            selectableRows={true}
                            multipleSelection={SQL_TABLE_CONFIGS[activeTable].multipleSelection}
                            deletableRows={SQL_TABLE_CONFIGS[activeTable].deletableRows}
                            deletionRequiresApproval={SQL_TABLE_CONFIGS[activeTable].deletionRequiresApproval}
                            enableFilters={true}
                            persistFilters={true}
                            sortOrder={SQL_TABLE_CONFIGS[activeTable].sortOrder}
                            showTable={showTable}
                            userRoleLevel={USER_ROLE_LEVEL[userRole] || 1}
                        />
                    )}
                    {/* ) : TABLE_CONFIGS[activeTable] ? (
                <ErrorBoundary fallback={<div>Error loading table</div>}>
                        {activeTable === 'main_table' && !selectedCarrier ? (
                            <div className="flex items-center justify-center h-64 text-gray-500">
                                Please select a carrier from the dropdown above to view data
                            </div>
                        ) : (
                            <TableComponent
                                data={sortedData}
                                columns={getTableColumns}
                                onCellEdit={handleCellEdit}
                                isEditLoading={isEditLoading}
                                columnFilters={columnFilters}
                                setColumnFilters={setColumnFilters}
                                selectedRows={selectedRows}
                                onRowSelect={handleRowSelect}
                                iconColumn={iconNeeded()}
                            />
                        )}
                </ErrorBoundary>
            ) : null} */}
                </div>
            )}
        </div>
    );
};

export default OpsTables;